import { useState, useEffect, useCallback } from 'react';
import { MapPin, Info, Mail } from 'lucide-react';
import employeeService from '../../../services/api/employeeService';
import timeClockService from '../../../services/api/timeClockService';
import eventService from '../../../services/api/eventService';
import PermissionGate from '../../shared/PermissionGate';
import { PERMISSIONS } from '../../../constants/permissionMappings';
import { US_STATES_OPTIONS } from '../../../utils/usStates';
import {
  LAST_NAME_MISSING_MESSAGE,
  shouldShowDriverNumber,
  shouldShowLastNameMissing
} from './employeeNameHints';

export {
  LAST_NAME_MISSING_MESSAGE,
  shouldShowDriverNumber,
  shouldShowLastNameMissing
};

/**
 * EmployeeDetailsTab - Employee personal and employment information
 *
 * Features:
 * - Personal Information: First/Last Name, Email, Phone
 * - Employment Information: Job Title, Employee Number, Driver Number (Driver role), Hire Date, Team
 * - Employment Status & Type: Status dropdown with termination flow
 * - Pay Classification & Work Location (Phase 8)
 * - Validation and error handling
 * - Termination confirmation modal
 */
function EmployeeDetailsTab({ employee, onEmployeeUpdated, onClose, onUnsavedChanges }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    jobTitle: '',
    employeeNumber: '',
    hireDate: '',
    teamId: '',
    employmentStatus: 'active',
    employmentType: 'full_time',
    terminationReason: '',
    // Phase 8: Pay classification & work arrangement
    payClassification: 'hourly',
    workArrangement: 'on_site',
    remoteWorkState: '',
    locationIds: [],
    primaryLocationId: ''
  });
  const [showTerminationReason, setShowTerminationReason] = useState(false);
  const [pinValue, setPinValue] = useState('');
  const [pinError, setPinError] = useState('');
  const [pinSuccess, setPinSuccess] = useState('');
  const [pinLoading, setPinLoading] = useState(false);
  const [clockPinSetAt, setClockPinSetAt] = useState(null);

  // Invite resend state
  const [resendingInvite, setResendingInvite] = useState(false);
  const [inviteMessage, setInviteMessage] = useState(null);

  // Company locations for multi-location assignment
  const [companyLocations, setCompanyLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(true);
  const [showLocationTooltip, setShowLocationTooltip] = useState(false);

  // Initialize form data when employee prop changes
  useEffect(() => {
    if (employee) {
      setFormData({
        firstName: employee.firstName || '',
        lastName: employee.lastName || '',
        email: employee.email || '',
        phone: employee.phone || '',
        jobTitle: employee.jobTitle || '',
        employeeNumber: employee.employeeNumber || '',
        hireDate: employee.hireDate ? employee.hireDate.split('T')[0] : '',
        teamId: employee.teamId || '',
        employmentStatus: employee.employmentStatus || 'active',
        employmentType: employee.employmentType || 'full_time',
        terminationReason: employee.terminationReason || '',
        // Phase 8 fields
        payClassification: employee.payClassification || 'hourly',
        workArrangement: employee.workArrangement || 'on_site',
        remoteWorkState: employee.remoteWorkState || '',
        locationIds: employee.locationIds || [],
        primaryLocationId: employee.primaryLocationId || ''
      });
      setShowTerminationReason(employee.employmentStatus === 'terminated');
      setClockPinSetAt(employee.clockPinSetAt || null);
    }
  }, [employee]);

  // Fetch company locations on mount
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        setLoadingLocations(true);
        const response = await eventService.getLocations();
        setCompanyLocations(response.locations || []);
      } catch (err) {
        console.error('Failed to fetch locations:', err);
        setCompanyLocations([]);
      } finally {
        setLoadingLocations(false);
      }
    };

    fetchLocations();
  }, []);

  // Track unsaved changes
  useEffect(() => {
    if (!employee) return;

    const hasChanges =
      formData.firstName !== (employee.firstName || '') ||
      formData.lastName !== (employee.lastName || '') ||
      formData.email !== (employee.email || '') ||
      formData.phone !== (employee.phone || '') ||
      formData.jobTitle !== (employee.jobTitle || '') ||
      formData.employeeNumber !== (employee.employeeNumber || '') ||
      formData.hireDate !== (employee.hireDate ? employee.hireDate.split('T')[0] : '') ||
      formData.teamId !== (employee.teamId || '') ||
      formData.employmentStatus !== (employee.employmentStatus || 'active') ||
      formData.employmentType !== (employee.employmentType || 'full_time') ||
      formData.terminationReason !== (employee.terminationReason || '') ||
      formData.payClassification !== (employee.payClassification || 'hourly') ||
      formData.workArrangement !== (employee.workArrangement || 'on_site') ||
      formData.remoteWorkState !== (employee.remoteWorkState || '') ||
      JSON.stringify(formData.locationIds) !== JSON.stringify(employee.locationIds || []) ||
      formData.primaryLocationId !== (employee.primaryLocationId || '');

    onUnsavedChanges(hasChanges);
  }, [formData, employee, onUnsavedChanges]);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');

    // Show/hide termination reason based on status
    if (name === 'employmentStatus') {
      setShowTerminationReason(value === 'terminated');
      if (value !== 'terminated') {
        setFormData(prev => ({ ...prev, terminationReason: '' }));
      }
    }
  };

  // Handle location checkbox toggle
  const handleLocationToggle = (locationId) => {
    setFormData(prev => {
      const isSelected = prev.locationIds.includes(locationId);
      let newLocationIds;
      let newPrimaryId = prev.primaryLocationId;

      if (isSelected) {
        newLocationIds = prev.locationIds.filter(id => id !== locationId);
        if (prev.primaryLocationId === locationId) {
          newPrimaryId = newLocationIds[0] || '';
        }
      } else {
        newLocationIds = [...prev.locationIds, locationId];
        if (newLocationIds.length === 1) {
          newPrimaryId = locationId;
        }
      }

      return { ...prev, locationIds: newLocationIds, primaryLocationId: newPrimaryId };
    });
  };

  // Set primary location
  const handleSetPrimaryLocation = (locationId) => {
    if (formData.locationIds.includes(locationId)) {
      setFormData(prev => ({ ...prev, primaryLocationId: locationId }));
    }
  };

  // Handle PIN set/reset
  const handleSetPin = async () => {
    setPinError('');
    setPinSuccess('');

    if (!/^\d{4}$/.test(pinValue)) {
      setPinError('PIN must be exactly 4 digits');
      return;
    }

    setPinLoading(true);
    try {
      const response = await timeClockService.setEmployeePin(employee.id, pinValue);
      setPinSuccess('Clock PIN updated successfully');
      setPinValue('');
      // Update local state with the new timestamp from API response
      if (response.clockPinSetAt) {
        setClockPinSetAt(response.clockPinSetAt);
      }
    } catch (err) {
      setPinError(err.response?.data?.error || 'Failed to set PIN');
    } finally {
      setPinLoading(false);
    }
  };

  // Handle resend invite
  const handleResendInvite = useCallback(async () => {
    if (!employee?.id) return;
    setResendingInvite(true);
    setInviteMessage(null);
    try {
      const response = await employeeService.resendInvite(employee.id);
      setInviteMessage({ type: 'success', text: response.message || 'Invitation resent successfully' });
    } catch (err) {
      setInviteMessage({ type: 'error', text: err.response?.data?.error || 'Failed to resend invitation' });
    } finally {
      setResendingInvite(false);
    }
  }, [employee?.id]);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (!formData.firstName.trim()) {
        throw new Error('First name is required');
      }
      const hasDriverNumber = employee.driverNumber != null
        && String(employee.driverNumber).trim() !== '';
      if (!formData.lastName.trim() && !hasDriverNumber) {
        throw new Error('Last name is required');
      }

      // If status changed to terminated, show confirmation modal
      if (formData.employmentStatus === 'terminated' && employee.employmentStatus !== 'terminated') {
        // TODO: Show termination confirmation modal
        // For now, proceed with update
      }

      // Prepare update data. Omit empty lastName for Utilization drivers so
      // a re-save does not trip LynxPay "last name cannot be empty".
      const updateData = {
        firstName: formData.firstName.trim(),
        email: formData.email.trim() || null,
        phone: formData.phone.trim() || null,
        jobTitle: formData.jobTitle.trim() || null,
        employeeNumber: formData.employeeNumber.trim() || null,
        hireDate: formData.hireDate || null,
        teamId: formData.teamId || null,
        employmentStatus: formData.employmentStatus,
        employmentType: formData.employmentType,
        terminationReason: formData.employmentStatus === 'terminated' ? formData.terminationReason : null,
        // Phase 8 fields
        payClassification: formData.payClassification,
        workArrangement: formData.workArrangement,
        remoteWorkState: (formData.workArrangement === 'remote' || formData.workArrangement === 'hybrid')
          ? formData.remoteWorkState
          : null,
        locationIds: formData.locationIds,
        primaryLocationId: formData.primaryLocationId || null
      };
      if (formData.lastName.trim()) {
        updateData.lastName = formData.lastName.trim();
      }

      await employeeService.updateEmployee(employee.id, updateData);

      // Don't update local state - modal is closing
      // Parent will handle data refresh after close

      if (onEmployeeUpdated) {
        onEmployeeUpdated();
      }

      // Modal will close via parent callback

    } catch (err) {
      console.error('Failed to update employee:', err);
      setError(err.response?.data?.error || err.message || 'Failed to update employee');
      setLoading(false);
    }
    // Don't reset loading state on success - let modal close with button still showing "Saving..."
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto px-6 py-6 space-y-8">
        {/* Error Message */}
        {error && (
          <div className="rounded-md bg-red-50 p-4 border border-red-200">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* Invite Status Banner */}
        {(employee?.invitationStatus === 'pending' || employee?.invitationStatus === 'expired') && (
          <div className="rounded-md bg-amber-50 p-4 border border-amber-200">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                <Mail className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-800">
                    This employee hasn't activated their account.
                  </p>
                  <p className="text-xs text-amber-700 mt-1">
                    {employee.invitationSentAt
                      ? `Invitation sent ${new Date(employee.invitationSentAt).toLocaleDateString()}`
                      : 'Invitation pending'}
                    {employee.invitationStatus === 'expired' && ' (expired)'}
                  </p>
                  {inviteMessage && (
                    <p className={`text-xs mt-1 ${inviteMessage.type === 'success' ? 'text-green-700' : 'text-red-700'}`}>
                      {inviteMessage.text}
                    </p>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={handleResendInvite}
                disabled={resendingInvite}
                className="flex-shrink-0 px-3 py-1.5 text-xs font-medium text-amber-800 bg-amber-100 border border-amber-300 rounded-md hover:bg-amber-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {resendingInvite ? 'Sending...' : 'Resend Invite'}
              </button>
            </div>
          </div>
        )}

        {/* Personal Information */}
        <div>
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Personal Information</h3>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                  First Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name
                  {!shouldShowLastNameMissing(employee, formData.lastName) && (
                    <span className="text-red-500"> *</span>
                  )}
                </label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  required={!shouldShowLastNameMissing(employee, formData.lastName)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
                {shouldShowLastNameMissing(employee, formData.lastName) && (
                  <p className="mt-1 text-xs text-amber-700">{LAST_NAME_MISSING_MESSAGE}</p>
                )}
              </div>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                Phone Number
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="(555) 123-4567"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Employment Information */}
        <div>
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Employment Information</h3>
          <div className="space-y-4">
            <div>
              <label htmlFor="jobTitle" className="block text-sm font-medium text-gray-700 mb-1">
                Job Title
              </label>
              <input
                type="text"
                id="jobTitle"
                name="jobTitle"
                value={formData.jobTitle}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label htmlFor="employeeNumber" className="block text-sm font-medium text-gray-700 mb-1">
                Employee Number
              </label>
              <input
                type="text"
                id="employeeNumber"
                name="employeeNumber"
                value={formData.employeeNumber}
                onChange={handleChange}
                placeholder="EMP-001"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {shouldShowDriverNumber(employee) && (
              <div>
                <label htmlFor="driverNumber" className="block text-sm font-medium text-gray-700 mb-1">
                  Driver Number
                </label>
                <input
                  type="text"
                  id="driverNumber"
                  name="driverNumber"
                  value={employee.driverNumber}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-50 text-gray-700"
                />
              </div>
            )}

            <div>
              <label htmlFor="hireDate" className="block text-sm font-medium text-gray-700 mb-1">
                Hire Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                id="hireDate"
                name="hireDate"
                value={formData.hireDate}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* TODO: Add Team Assignment dropdown when teams are implemented */}
          </div>
        </div>

        {/* Employment Status & Type */}
        <div>
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Employment Status & Type</h3>
          <div className="space-y-4">
            <div>
              <label htmlFor="employmentStatus" className="block text-sm font-medium text-gray-700 mb-1">
                Employment Status
              </label>
              <select
                id="employmentStatus"
                name="employmentStatus"
                value={formData.employmentStatus}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="active">Active - Full system access</option>
                <option value="inactive">Inactive - Temporarily disabled</option>
                <option value="terminated">Terminated - Employment ended</option>
              </select>
            </div>

            {/* Termination Reason (conditional) */}
            {showTerminationReason && (
              <div className="bg-amber-50 border border-amber-200 rounded-md p-4">
                <div className="flex gap-2 mb-3">
                  <svg className="h-5 w-5 text-amber-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <p className="text-sm text-amber-800">
                    Terminating this employee will immediately revoke all system access.
                  </p>
                </div>
                <label htmlFor="terminationReason" className="block text-sm font-medium text-gray-700 mb-1">
                  Termination Reason (Optional)
                </label>
                <textarea
                  id="terminationReason"
                  name="terminationReason"
                  value={formData.terminationReason}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Voluntary resignation, accepted position with competitor..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            )}

            <div>
              <label htmlFor="employmentType" className="block text-sm font-medium text-gray-700 mb-1">
                Employment Type
              </label>
              <select
                id="employmentType"
                name="employmentType"
                value={formData.employmentType}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="full_time">Full-Time</option>
                <option value="part_time">Part-Time</option>
                <option value="contract">Contract</option>
              </select>
            </div>
          </div>
        </div>

        {/* Pay Classification & Work Location */}
        <div>
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Pay Classification & Work Location</h3>
          <div className="space-y-4">
            {/* Pay Classification */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Pay Classification
              </label>
              <div className="flex gap-4 flex-wrap">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="payClassification"
                    value="hourly"
                    checked={formData.payClassification === 'hourly'}
                    onChange={handleChange}
                    className="mr-2 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Hourly</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="payClassification"
                    value="salaried"
                    checked={formData.payClassification === 'salaried'}
                    onChange={handleChange}
                    className="mr-2 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Salaried</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="payClassification"
                    value="contractor"
                    checked={formData.payClassification === 'contractor'}
                    onChange={handleChange}
                    className="mr-2 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Contractor (1099)</span>
                </label>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                {formData.payClassification === 'hourly' && 'Hourly employees must clock in/out and are eligible for overtime.'}
                {formData.payClassification === 'salaried' && 'Salaried employees appear in the portal but cannot clock in/out.'}
                {formData.payClassification === 'contractor' && 'Contractors are hidden from the time clock portal and do not receive PTO.'}
              </p>
            </div>

            {/* Work Arrangement */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="workArrangement" className="block text-sm font-medium text-gray-700 mb-1">
                  Work Arrangement
                </label>
                <select
                  id="workArrangement"
                  name="workArrangement"
                  value={formData.workArrangement}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="on_site">On Site</option>
                  <option value="remote">Remote</option>
                  <option value="hybrid">Hybrid</option>
                </select>
              </div>

              {/* Remote Work State */}
              {(formData.workArrangement === 'remote' || formData.workArrangement === 'hybrid') && (
                <div>
                  <label htmlFor="remoteWorkState" className="block text-sm font-medium text-gray-700 mb-1">
                    Remote Work State <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="remoteWorkState"
                    name="remoteWorkState"
                    value={formData.remoteWorkState}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select state...</option>
                    {US_STATES_OPTIONS.map(state => (
                      <option key={state.value} value={state.value}>{state.label}</option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    Required for labor law compliance.
                  </p>
                </div>
              )}
            </div>

            {/* Work Locations */}
            {companyLocations.length > 1 && (formData.workArrangement === 'on_site' || formData.workArrangement === 'hybrid') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Assigned Locations
                  <button
                    type="button"
                    className="ml-1 inline-flex relative"
                    onMouseEnter={() => setShowLocationTooltip(true)}
                    onMouseLeave={() => setShowLocationTooltip(false)}
                  >
                    <Info className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                    {showLocationTooltip && (
                      <div className="absolute bottom-full left-0 mb-2 w-64 p-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg z-10">
                        <p>Primary location is used as the default when creating shifts.</p>
                      </div>
                    )}
                  </button>
                </label>
                <div className="border border-gray-300 rounded-md p-3 space-y-2">
                  {loadingLocations ? (
                    <p className="text-sm text-gray-500">Loading locations...</p>
                  ) : (
                    companyLocations.map(location => (
                      <div key={location.id} className="flex items-center justify-between">
                        <label className="flex items-center flex-1">
                          <input
                            type="checkbox"
                            checked={formData.locationIds.includes(location.id)}
                            onChange={() => handleLocationToggle(location.id)}
                            className="mr-2 rounded text-blue-600 focus:ring-blue-500"
                          />
                          <MapPin className="h-4 w-4 text-gray-400 mr-1" />
                          <span className="text-sm text-gray-700">
                            {location.name}
                            {location.city && ` (${location.city}, ${location.state})`}
                          </span>
                        </label>
                        {formData.locationIds.includes(location.id) && (
                          <button
                            type="button"
                            onClick={() => handleSetPrimaryLocation(location.id)}
                            className={`text-xs px-2 py-1 rounded ${
                              formData.primaryLocationId === location.id
                                ? 'bg-blue-100 text-blue-700 font-medium'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                          >
                            {formData.primaryLocationId === location.id ? 'Primary' : 'Set Primary'}
                          </button>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Employee Portal PIN - for hourly and salaried employees (not contractors) */}
        {formData.payClassification !== 'contractor' && (
        <PermissionGate permission={PERMISSIONS.time_clock.manage_pins}>
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-4">Employee Portal PIN</h3>
            <div className="space-y-3">
              <p className="text-sm text-gray-500">
                {clockPinSetAt
                  ? `PIN last set on ${new Date(clockPinSetAt).toLocaleDateString()}`
                  : 'No portal PIN has been set for this employee'}
              </p>
              <div className="flex items-center gap-3">
                <input
                  type="text"
                  value={pinValue}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 4);
                    setPinValue(val);
                    setPinError('');
                    setPinSuccess('');
                  }}
                  inputMode="numeric"
                  maxLength={4}
                  placeholder="Enter 4 digit PIN"
                  className={`w-48 px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                    pinError ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                <button
                  type="button"
                  onClick={handleSetPin}
                  disabled={pinLoading || !pinValue}
                  className="px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {pinLoading ? 'Setting...' : (clockPinSetAt ? 'Reset PIN' : 'Set PIN')}
                </button>
              </div>
              {pinError && <p className="text-sm text-red-600">{pinError}</p>}
              {pinSuccess && <p className="text-sm text-green-600">{pinSuccess}</p>}
            </div>
          </div>
        </PermissionGate>
        )}
      </div>

      {/* Footer Actions */}
      <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 flex justify-end gap-3">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </form>
  );
}

export default EmployeeDetailsTab;
